import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import config from '../config/appConfig';
import { apicFetch } from '../services/apiTokenService';
import { isLocalDevMode } from '../utils/devMode';
import { CONTACT_US_URL_OVERRIDES, DEFAULT_CONTACT_US_URL } from '../components/errorMessages/errorMessages.constants';
import { dummyPaPlanFeaturesResponse } from '../data/dummyPaData';

const PlanFeaturesContext = createContext();

export const usePlanFeatures = () => {
  const context = useContext(PlanFeaturesContext);
  if (!context) {
    throw new Error('usePlanFeatures must be used within a PlanFeaturesProvider');
  }
  return context;
};

export const PlanFeaturesProvider = ({ children }) => {
  const [planFeatures, setPlanFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [environmentId, setEnvironmentId] = useState(null); // null until set from API or fallback
  const [planCode, setPlanCodeState] = useState(null); // Start as null, will be set from URL or fallback
  const [initialized, setInitialized] = useState(false); // Track if we've done initial load

  // On localhost, always use the hardcoded PA (environmentId "28") dummy plan-features
  // response instead of hitting the real API (via the /api-proxy Vite dev proxy, which
  // requires a working connection to the devintaz-oci backend). Applies regardless of
  // which envId/planCode was requested — local dev is hardcoded to PA per request.
  const useDummyPlanFeatures = () => {
    console.log('🧪 Using dummy PA plan features (local dev mode) — skipping real API call');
    const primaryFeature = dummyPaPlanFeaturesResponse.planFeatures?.[0] || null;
    setPlanFeatures(primaryFeature);
    setError('');
    if (primaryFeature?.environmentId) {
      setEnvironmentId(primaryFeature.environmentId);
    }
    setInitialized(true);
    setLoading(false);
  };

  const fetchPlanFeatures = async (envId = environmentId) => {
    if (isLocalDevMode()) {
      useDummyPlanFeatures();
      return;
    }

    console.log('🔧 PlanFeatures API Call Starting:', {
      requestedEnvId: envId,
      currentEnvId: environmentId,
      timestamp: new Date().toISOString()
    });
    
    setLoading(true);
    setError('');

    // Construct environment-specific API URL
    let apiUrl;
    if (import.meta.env.DEV && window.location.hostname === 'localhost') {
      // Use proxy path for local development to avoid CORS
      apiUrl = `/api-proxy/hcb/v3/planfeature/plans/${envId}/features`;
    } else {
      // Use direct API URL for other environments
      apiUrl = `${config.APIC_BASE_URL_API}/hcb/v3/planfeature/plans/${envId}/features`;

    }

    try {
      const response = await apicFetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      console.log('🔧 PlanFeatures API Results:', {
        url: apiUrl,
        envId: envId,
        response: data,
        planFeaturesCount: data.planFeatures?.length || 0,
        primaryFeature: data.planFeatures?.[0] || null
      });
      
      // Take the first plan feature as the primary configuration
      const primaryFeature = data.planFeatures?.[0] || null;
      setPlanFeatures(primaryFeature);
      
      // Update environmentId if present in the response
      if (primaryFeature?.environmentId) {
        console.log('🔧 Updating environmentId from API response:', {
          oldEnvId: environmentId,
          newEnvId: primaryFeature.environmentId,
          source: 'fetchPlanFeatures'
        });
        setEnvironmentId(primaryFeature.environmentId);
      }

    } catch (err) {
      setError(`Unable to load plan features: ${err.message}`);
      setPlanFeatures(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch plan features by plan code
  const fetchPlanFeaturesByCode = async (code) => {
    if (isLocalDevMode()) {
      useDummyPlanFeatures();
      return;
    }

    console.log('🔧 PlanFeatures by Code API Call Starting:', {
      planCode: code,
      timestamp: new Date().toISOString()
    });
    
    setLoading(true);
    setError('');

    // Construct plan-code-specific API URL using same format as regular API
    let apiUrl;
    if (import.meta.env.DEV && window.location.hostname === 'localhost') {
      // Use proxy path for local development to avoid CORS
      apiUrl = `/api-proxy/hcb/v3/planfeature/plans/${code}/features`;
    } else {
      // Use direct API URL for other environments
      apiUrl = `${config.APIC_BASE_URL_API}/hcb/v3/planfeature/plans/${code}/features`;
    }

    try {
      const response = await apicFetch(apiUrl);

      if (!response.ok) {
        const httpError = new Error(`HTTP ${response.status}: ${response.statusText}`);
        httpError.status = response.status;
        throw httpError;
      }

      const data = await response.json();
      
      console.log('🔧 PlanFeatures by Code API Results:', {
        url: apiUrl,
        planCode: code,
        response: data,
        planFeaturesCount: data.planFeatures?.length || 0,
        primaryFeature: data.planFeatures?.[0] || null
      });
      
      // Take the first plan feature as the primary configuration (same as regular API)
      const primaryFeature = data.planFeatures?.[0] || null;

      // A code that doesn't match any known state/state-DNSP plan comes back from the
      // API with an empty planFeatures array (no HTTP error, just nothing to show).
      // Treat that the same as an invalid code and redirect to the generic plan
      // picker instead of leaving the app stuck with no plan configuration.
      if (!primaryFeature) {
        console.warn(`⚠️ No plan features found for code "${code}" — redirecting to plan picker`);
        redirectInvalidPlanCode(code);
        return;
      }

      setPlanFeatures(primaryFeature);
      
      // Update environmentId if present in the response
      if (primaryFeature?.environmentId) {
        console.log('🔧 Updating environmentId from API response:', {
          oldEnvId: environmentId,
          newEnvId: primaryFeature.environmentId,
          planCode: code,
          planName: primaryFeature.planName,
          source: 'fetchPlanFeaturesByCode'
        });
        setEnvironmentId(primaryFeature.environmentId);
      }

    } catch (err) {
      console.error('❌ Failed to fetch plan features by code:', err);
      // Only redirect when the API has definitively told us this plan code doesn't
      // exist (HTTP 404). Every other failure — 5xx, APIC auth/token problems,
      // timeouts, CORS/network errors — is an outage, not an invalid code, and must
      // NOT bounce the user to the plan picker (an earlier version redirected on any
      // error here, which sent every page in QA to the plan picker during an API
      // blip). Those fall back to the pre-existing error state instead.
      if (err?.status === 404) {
        redirectInvalidPlanCode(code);
      } else {
        setError(`Unable to load plan features for ${code}: ${err.message}`);
        setPlanFeatures(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Redirects to the generic plan-selection screen when a plan code in the URL
  // doesn't correspond to a real state/state-DNSP. Mirrors App.jsx's
  // redirectToPlanPicker() behavior: falls back to leaving the app in its current
  // (errored) state when no PLAN_PICKER_URL is configured for this tier (dev/local).
  const redirectInvalidPlanCode = (code) => {
    setPlanFeatures(null);
    if (!config.PLAN_PICKER_URL) {
      console.warn(`[PlanFeaturesContext] Invalid plan code "${code}" and no PLAN_PICKER_URL configured for this environment (dev/local) — staying put.`);
      setError(`Unable to load plan features for ${code}`);
      return;
    }
    window.location.replace(config.PLAN_PICKER_URL);
  };

  // Wait for plan code from URL — no automatic fallback fetch.
  // If no plan code arrives within 500ms, something went wrong upstream.
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (!initialized && !planCode) {
        console.warn('⚠️ No plan code received from URL after 500ms — plan features will not load');
      }
    }, 500);

    return () => clearTimeout(fallbackTimer);
  }, [initialized, planCode]);

  // Update environment ID and refetch when it changes
  const updateEnvironmentId = (newEnvId) => {
    console.log('🔧 updateEnvironmentId called:', {
      newEnvId,
      currentEnvId: environmentId,
      willUpdate: newEnvId && newEnvId !== environmentId
    });
    
    // Only update and refetch if the environment ID actually changed
    if (newEnvId && newEnvId !== environmentId) {
      setEnvironmentId(newEnvId);
      fetchPlanFeatures(newEnvId);
    }
  };

  // Update plan code and refetch when it changes
  const setPlanCode = (newPlanCode) => {
    console.log('🔧 setPlanCode called:', {
      newPlanCode,
      currentPlanCode: planCode,
      initialized,
      willUpdate: newPlanCode && newPlanCode !== planCode
    });
    
    // Only update and refetch if the plan code actually changed
    if (newPlanCode && newPlanCode !== planCode) {
      // Mark as initialized IMMEDIATELY to prevent fallback race condition
      setInitialized(true);
      setPlanCodeState(newPlanCode);
      // Fetch plan features for the new plan code
      fetchPlanFeaturesByCode(newPlanCode);
    } else if (newPlanCode && !initialized) {
      // If same plan code but not initialized yet, still mark as initialized
      // This prevents fallback from running
      console.log('🔧 Setting initialized=true for existing plan code to prevent fallback');
      setInitialized(true);
    }
  };

  // Helper functions to extract specific configuration values
  const getFooterLinks = () => {
    if (!planFeatures) return {};
    
    return {
      contactUs: planFeatures.contactUsURL || '',
      privacyPolicy: planFeatures.privacyPolicyURL || '',
      legalStatement: planFeatures.legalStatementURL || '',
      nonDiscrimination: planFeatures.nonDiscriminationNoticeURL || '',
      // Mercy Care (AZ) footer replaces the standard 3-link set (Privacy policy /
      // Legal statement / Nondiscrimination notice) with its own 3 links: a combined
      // "Privacy and legal" link, plus TWO separate nondiscrimination notices — one for
      // the base Mercy Care plan and one for the Mercy Care Advantage plan. These are
      // distinct URLs from the standard nonDiscrimination one above, so they need their
      // own plan-features fields rather than reusing it.
      mercyCarePrivacyAndLegal: planFeatures.privacyPolicyURL || '',
      mercyCareNonDiscrimination: planFeatures.nonDiscriminationNoticeURL || '',
      mercyCareAdvantageNonDiscrimination: planFeatures.legalStatementURL || '',
      logoRedirect: planFeatures.logoRedirectURL || '',
      printDirectory: planFeatures.printDirectoryURL || ''
    };
  };

  const getBrowseByCategories = () => {
    if (!planFeatures?.browseByCategories) return [];
    
    // Split by | or comma and clean up
    return planFeatures.browseByCategories
      .split(/[|,]/)
      .map(category => category.trim())
      .filter(category => category.length > 0);
  };

  const getPopularSearches = () => {
    // Check both popularSearches (plural) and popularSearch (singular) for API compatibility
    const popularSearchData = planFeatures?.popularSearches || planFeatures?.popularSearch;
    
    if (!popularSearchData) return [];
    
    // Check if popularSearch/popularSearches is an array of objects
    if (Array.isArray(popularSearchData)) {
      const mapped = popularSearchData
        .map(search => ({
          displayName: search.psName || search.displayName || search.name || '',
          value: search.psId || search.value || search.id || ''
        }))
        .filter(search => search.displayName && search.value); // Filter out entries with empty displayName or value
      
      console.log('🔍 Popular Searches Processed:', {
        rawData: popularSearchData,
        mapped: mapped,
        count: mapped.length
      });
      
      return mapped;
    }
    
    console.warn('⚠️ Popular search data is not an array:', popularSearchData);
    return [];
  };

  const getSearchResultFields = () => {
    // For now, return default fields - this will be expanded based on plan features
    return {
      showFax: false, // Will be controlled by plan features in the future
      showLanguages: true,
      showExtendedHours: true,
      showBoardCertification: true,
      showProviderType: true,
      showCounty: true
    };
  };

  const getStateName = () => {
    // Get state name from planDescription field
    if (planFeatures?.planDescription) {
      return planFeatures.planDescription;
    }
    
    return 'Your State';
  };

  // Returns the "Contact us" URL to use inside the new integration-error banners
  // (APIC/MCS backend, Google location, Akorbi translation, SMTP email — see
  // reports/SPECIFIC_ERROR_MESSAGES_PLAN.md). This intentionally does NOT just
  // return getFooterLinks().contactUs, because several plans currently have a
  // missing ("NA") or incorrectly-scoped contactUsURL (see
  // reports/PLAN_FEATURE_LINK_AUDIT.md) and Mercy Care / FIDE / HIDE plans use a
  // dedicated contact page instead of their plan-features contactUsURL.
  //
  // Priority: special-case override (by planName) -> raw contactUsURL from the API
  // (if it's a usable value) -> generic Aetna contact page as a last resort so the
  // link is never dead/"#".
  const getErrorContactUsUrl = () => {
    const planName = planFeatures?.planName;

    if (planName && CONTACT_US_URL_OVERRIDES[planName]) {
      return CONTACT_US_URL_OVERRIDES[planName];
    }

    const rawUrl = planFeatures?.contactUsURL;
    if (rawUrl && rawUrl !== 'NA') {
      return rawUrl;
    }

    return DEFAULT_CONTACT_US_URL;
  };

  const getAllowedLanguages = () => {
    // Check if allowedLanguages exists in plan features
    if (!planFeatures?.allowedLanguages) {
      // Default to English, Spanish, Urdu, Tamil if not specified
      console.log('No allowedLanguages in plan features, using defaults');
      return ['English'];
    }

    // If it's already an array, return it
    if (Array.isArray(planFeatures.allowedLanguages)) {
      return planFeatures.allowedLanguages.filter(lang => lang && lang.trim().length > 0);
    }

    // If it's a string, split by | or comma
    if (typeof planFeatures.allowedLanguages === 'string') {
      return planFeatures.allowedLanguages
        .split(/[|,]/)
        .map(lang => lang.trim())
        .filter(lang => lang.length > 0);
    }

    console.warn('allowedLanguages has unexpected format:', planFeatures.allowedLanguages);
    return ['English'];
  };

  const getStateOptionsData = () => {
    // Extract provider info from plan features
    if (!planFeatures?.moreProviderInfo) {
      console.log('No moreProviderInfo found in plan features, returning empty array');
      return [];
    }

    // If moreProviderInfo is an array of objects with label and content
    if (Array.isArray(planFeatures.moreProviderInfo)) {
      return planFeatures.moreProviderInfo
        .map((info, index) => ({
          id: info.id || `state-option-${index + 1}`,
          label: info.label || info.title || info.name || `State option ${index + 1}`,
          contentHTML: info.content || info.description || info.text || info.contentHTML || ''
        }))
        .filter(option => option.label && option.contentHTML); // Filter out incomplete entries
    }

    // If moreProviderInfo is an object (single item)
    if (typeof planFeatures.moreProviderInfo === 'object') {
      return [{
        id: planFeatures.moreProviderInfo.id || 'state-option-1',
        label: planFeatures.moreProviderInfo.label || planFeatures.moreProviderInfo.title || 'State option 1',
        content: planFeatures.moreProviderInfo.content || planFeatures.moreProviderInfo.description || ''
      }];
    }

    if (typeof planFeatures.moreProviderInfo === 'string' && planFeatures.moreProviderInfo.trim() === 'NA') {
      return [];
    }

    console.warn('moreProviderInfo has unexpected format:', planFeatures.moreProviderInfo);
    return [];
  };

  const getCentralZip = () => {
    return planFeatures?.centralZip || null;
  };

  // Feature Flags Helper Functions - Memoized to prevent excessive recalculation
  const featureFlags = useMemo(() => {
    if (!planFeatures?.featureFlags) {
      console.warn('No feature flags found in plan features, returning defaults (all enabled)');
      return {
        filters: {},
        providerCard: {},
        providerDetails: {},
        serviceLocations: {},
        sections: {
          browseBy: 'Y',
          popularSearch: 'Y',
          stateOptions: 'Y'
        }
      };
    }

    console.log('Feature Flags Loaded:', planFeatures.featureFlags);
    
    // Ensure sections category exists with defaults if not provided by API
    const flags = { ...planFeatures.featureFlags };
    if (!flags.sections) {
      console.log('Sections category not found in API response, adding defaults');
      flags.sections = {
        browseBy: 'Y',
        popularSearch: 'Y',
        stateOptions: 'Y'
      };
    } else {
      // Ensure individual section flags exist with defaults
      if (flags.sections.browseBy === undefined) flags.sections.browseBy = 'Y';
      if (flags.sections.popularSearch === undefined) flags.sections.popularSearch = 'Y';
      if (flags.sections.stateOptions === undefined) flags.sections.stateOptions = 'Y';
    }
    
    return flags;
  }, [planFeatures?.featureFlags]);

  // Getter function for backwards compatibility
  const getFeatureFlags = () => featureFlags;

  // Check if a specific feature is enabled (Y) or disabled (N)
  // Default behavior: If feature flag doesn't exist or is undefined, returns `defaultValue`
  // (TRUE/enabled unless the caller opts into a stricter default) — this ensures backward
  // compatibility and graceful degradation for the many fields that were already audited
  // and shipped under a fail-open policy (see reports/FEATURE_FLAGS_AUDIT.md AC5).
  //
  // Newer, opt-in-by-design fields (like providerCard.virtualCareModal — see
  // isProviderCardFieldEnabled's call site in providerSummary.jsx) should instead pass
  // `defaultValue: false` so a plan that has never had the key added to its API response
  // yet stays hidden until someone explicitly turns it on for that plan, rather than
  // silently appearing everywhere the moment this frontend ships. This was reported for
  // plan PA, whose plan-features response simply doesn't define
  // providerCard.virtualCareModal at all — previously that fell through to this
  // function's fail-open default and showed the link even though PA was never configured
  // to have it.
  const isFeatureEnabled = (category, featureName, defaultValue = true) => {
    const featureValue = featureFlags[category]?.[featureName];
    
    // If feature flag doesn't exist, fall back to the caller-specified default —
    // TRUE (enabled) unless the caller opts into a stricter, fail-closed default.
    if (featureValue === undefined || featureValue === null) {
      return defaultValue;
    }
    
    // Only 'Y' (case-insensitive) enables the feature, all other values disable it
    return featureValue.toUpperCase() === 'Y';
  };

  // Convenience methods for specific categories. Each accepts an optional `defaultValue`
  // (forwarded to isFeatureEnabled above) for the rare field that needs to fail closed
  // instead of the standard fail-open default — every existing call site keeps its exact
  // current behavior unchanged since defaultValue defaults to true here too.
  const isFilterEnabled = (filterName, defaultValue = true) => isFeatureEnabled('filters', filterName, defaultValue);
  const isProviderCardFieldEnabled = (fieldName, defaultValue = true) => isFeatureEnabled('providerCard', fieldName, defaultValue);
  const isProviderDetailsFieldEnabled = (fieldName, defaultValue = true) => isFeatureEnabled('providerDetails', fieldName, defaultValue);
  const isServiceLocationFieldEnabled = (fieldName, defaultValue = true) => isFeatureEnabled('serviceLocations', fieldName, defaultValue);
  
  // Page sections feature flags (browseBy, popularSearch, stateOptions)
  const isSectionEnabled = (sectionName) => isFeatureEnabled('sections', sectionName);

  // `featureFlags.emergencyCareBrowseBy` is a TOP-LEVEL flag on the featureFlags object
  // (a sibling of the filters/providerCard/providerDetails/serviceLocations/sections
  // categories), not nested under one of them, so it can't go through isFeatureEnabled's
  // category lookup. Controls whether the "Emergency and Urgent Care" entry (button +
  // 911 informational copy) shows in the Browse by Care side sheet. Written in reverse
  // (fail-OPEN): only an explicit 'N' hides it — a missing/undefined/blank flag leaves
  // the entry visible so existing plans are unaffected until they opt out.
  const isEmergencyCareBrowseByEnabled = () => {
    const value = featureFlags?.emergencyCareBrowseBy;
    if (value === undefined || value === null || value === '') return true;
    return String(value).toUpperCase() !== 'N';
  };

  // Toggle feature flag for testing (accessible from browser console)
  const toggleFeatureFlag = (featurePath) => {
    if (!planFeatures?.featureFlags) {
      console.error('❌ No feature flags available to toggle');
      return;
    }

    // Parse the feature path (e.g., "sections.browseBy" or just "browseBy")
    let category, featureName;
    
    if (featurePath.includes('.')) {
      [category, featureName] = featurePath.split('.');
    } else {
      // Auto-detect category by searching all categories
      const categories = ['sections', 'filters', 'providerCard', 'providerDetails', 'serviceLocations'];
      for (const cat of categories) {
        if (planFeatures.featureFlags[cat]?.[featurePath] !== undefined) {
          category = cat;
          featureName = featurePath;
          break;
        }
      }
      
      if (!category) {
        console.error(`❌ Feature "${featurePath}" not found in any category. Use format: "category.feature" or ensure feature exists.`);
        console.log('Available features:', planFeatures.featureFlags);
        return;
      }
    }

    // Validate category exists
    if (!planFeatures.featureFlags[category]) {
      console.error(`❌ Category "${category}" not found. Available categories:`, Object.keys(planFeatures.featureFlags));
      return;
    }

    // Get current value
    const currentValue = planFeatures.featureFlags[category][featureName];
    
    if (currentValue === undefined) {
      console.warn(`⚠️ Feature "${featureName}" not found in category "${category}". Creating new flag...`);
    }

    // Toggle the value (Y -> N or N -> Y, undefined -> Y)
    const newValue = (currentValue?.toUpperCase() === 'Y') ? 'N' : 'Y';

    // Update the state
    setPlanFeatures(prev => ({
      ...prev,
      featureFlags: {
        ...prev.featureFlags,
        [category]: {
          ...prev.featureFlags[category],
          [featureName]: newValue
        }
      }
    }));

    console.log(`✅ Feature flag toggled: ${category}.${featureName}`);
    console.log(`   Previous value: ${currentValue || 'undefined (default: enabled)'}`);
    console.log(`   New value: ${newValue}`);
    console.log(`   Feature is now: ${newValue === 'Y' ? '✅ ENABLED' : '❌ DISABLED'}`);
  };

  // Expose toggle function globally for console access
  useEffect(() => {
    window.featureToggle = (featurePath) => {
      console.log(`🔧 Feature Toggle Request: "${featurePath}"`);
      toggleFeatureFlag(featurePath);
    };

    // Also expose helper to list all features
    window.listFeatures = () => {
      if (!planFeatures?.featureFlags) {
        console.log('❌ No feature flags loaded yet');
        return;
      }
      
      console.log('📋 Available Feature Flags:');
      Object.entries(planFeatures.featureFlags).forEach(([category, features]) => {
        console.log(`\n${category.toUpperCase()}:`);
        Object.entries(features).forEach(([name, value]) => {
          const status = (value?.toUpperCase() === 'Y') ? '✅ Enabled' : '❌ Disabled';
          console.log(`  - ${name}: ${value} ${status}`);
        });
      });
      console.log('\nUsage: featureToggle("featureName") or featureToggle("category.featureName")');
    };

    // Cleanup on unmount
    return () => {
      delete window.featureToggle;
      delete window.listFeatures;
    };
  }, [planFeatures, toggleFeatureFlag]);

  const contextValue = {
    // State
    planFeatures,
    loading,
    error,
    environmentId,
    planCode,

    // Actions
    updateEnvironmentId,
    setPlanCode,
    refetchPlanFeatures: () => fetchPlanFeatures(),

    // Helper functions
    getFooterLinks,
    getErrorContactUsUrl,
    getBrowseByCategories,
    getPopularSearches,
    getSearchResultFields,
    getStateName,
    getAllowedLanguages,
    getStateOptionsData,
    getCentralZip,

    // Feature Flags
    getFeatureFlags,
    isFeatureEnabled,
    isFilterEnabled,
    isProviderCardFieldEnabled,
    isProviderDetailsFieldEnabled,
    isServiceLocationFieldEnabled,
    isSectionEnabled,
    isEmergencyCareBrowseByEnabled,
    toggleFeatureFlag, // For testing/debugging

    // Raw access
    getPlanFeatures: () => planFeatures
  };

  return (
    <PlanFeaturesContext.Provider value={contextValue}>
      {children}
    </PlanFeaturesContext.Provider>
  );
};

export default PlanFeaturesProvider;
